/**
 * WebSocket Client — manages connection to the game server for multi-device mode.
 * New model: devices have roles (reader/scoreboard), players are just names managed by host.
 */

import { ClientMessage, ServerMessage, SyncedGameState } from '../shared/protocol';
import { DeviceRole, ScoreboardAffinity, Team } from '../shared/types';

type StateListener = (state: SyncedGameState) => void;
type EventListener = (msg: ServerMessage) => void;

const WS_URL = import.meta.env.VITE_WS_ENDPOINT || 'ws://localhost:3001';

let ws: WebSocket | null = null;
let myConnectionId: string | null = null;
let sessionCode: string | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT = 5;

const stateListeners = new Set<StateListener>();
const eventListeners = new Set<EventListener>();

// --- Device ID persistence for rejoin ---
const DEVICE_ID_KEY = 'papelitos-device-id';
const SESSION_KEY = 'papelitos-active-session';

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = 'dev-' + crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function getDeviceId(): string {
  return getOrCreateDeviceId();
}

function saveActiveSession(code: string): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ code, deviceId: getOrCreateDeviceId(), timestamp: Date.now() }));
}

function clearActiveSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

export function getActiveSession(): { code: string; deviceId: string; timestamp: number } | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 1 hour
    if (Date.now() - data.timestamp > 3600000) {
      clearActiveSession();
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

// --- Public API ---

export function getConnectionId(): string | null {
  return myConnectionId;
}

export function getSessionCode(): string | null {
  return sessionCode;
}

export function isConnected(): boolean {
  return ws !== null && ws.readyState === WebSocket.OPEN;
}

export function onStateUpdate(fn: StateListener): () => void {
  stateListeners.add(fn);
  return () => stateListeners.delete(fn);
}

export function onServerEvent(fn: EventListener): () => void {
  eventListeners.add(fn);
  return () => eventListeners.delete(fn);
}

export function connect(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      resolve();
      return;
    }

    ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      console.log('[ws] Connected to server');
      reconnectAttempts = 0;
      resolve();
    };

    ws.onmessage = (event) => {
      try {
        const msg: ServerMessage = JSON.parse(event.data);
        handleServerMessage(msg);
      } catch (err) {
        console.error('[ws] Failed to parse message:', err);
      }
    };

    ws.onclose = () => {
      console.log('[ws] Connection closed');
      ws = null;
      attemptReconnect();
    };

    ws.onerror = (err) => {
      console.error('[ws] Error:', err);
      reject(err);
    };
  });
}

export function disconnect(): void {
  if (ws) {
    ws.close();
    ws = null;
  }
  myConnectionId = null;
  sessionCode = null;
  reconnectAttempts = MAX_RECONNECT; // prevent auto-reconnect
}

export function sendMessage(msg: ClientMessage): void {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.warn('[ws] Not connected, cannot send:', msg.type);
    return;
  }
  ws.send(JSON.stringify(msg));
}

// --- Convenience methods ---

export function createSession(hostName: string): void {
  sendMessage({ type: 'create-session', hostName, deviceId: getOrCreateDeviceId() });
}

export function joinSession(code: string, requestedRole?: 'reader' | 'scoreboard'): void {
  sessionCode = code; // Store locally so getSessionCode() works for joiners
  sendMessage({ type: 'join-session', sessionCode: code, requestedRole, deviceId: getOrCreateDeviceId() });
}

export function rejoinSession(code: string): void {
  sessionCode = code;
  sendMessage({ type: 'rejoin-session', sessionCode: code, deviceId: getOrCreateDeviceId() });
}

export function assignDeviceRole(targetConnectionId: string, role: 'reader' | 'scoreboard', teamAffinity?: ScoreboardAffinity): void {
  sendMessage({ type: 'assign-device-role', targetConnectionId, role, teamAffinity });
}

export function assignTeam(playerId: string, team: Team): void {
  sendMessage({ type: 'assign-team', playerId, team });
}

export function removePlayer(playerId: string): void {
  sendMessage({ type: 'remove-player', playerId });
}

export function addPlayer(playerName: string, team: Team | null = null): void {
  sendMessage({ type: 'add-player', playerName, team });
}

export function startSetup(): void {
  sendMessage({ type: 'start-setup' });
}

export function updateSettings(settings: Record<string, unknown>): void {
  sendMessage({ type: 'update-settings', settings: settings as any });
}

export function selectDeck(deckId: string): void {
  sendMessage({ type: 'select-deck', deckId });
}

export function startGame(): void {
  sendMessage({ type: 'start-game' });
}

export function startTurn(): void {
  sendMessage({ type: 'start-turn' });
}

export function advanceToTurnWaiting(): void {
  sendMessage({ type: 'advance-to-turn-waiting' });
}

export function resumeMidRound(): void {
  sendMessage({ type: 'resume-mid-round' });
}

export function nextCard(): void {
  sendMessage({ type: 'next-card' });
}

export function skipCard(): void {
  sendMessage({ type: 'skip-card' });
}

export function goBack(): void {
  sendMessage({ type: 'go-back' });
}

export function skipProtests(): void {
  sendMessage({ type: 'skip-protests' });
}

export function voteProtest(protestIndex: number, team: Team, vote: 'valid' | 'invalid'): void {
  sendMessage({ type: 'vote-protest', protestIndex, team, vote });
}

export function markProtest(cardTitle: string): void {
  sendMessage({ type: 'mark-protest', cardTitle });
}

export function hostResolveProtest(protestIndex: number, decision: 'upheld' | 'dismissed'): void {
  sendMessage({ type: 'host-resolve-protest', protestIndex, decision });
}

export function advanceTurn(): void {
  sendMessage({ type: 'advance-turn' });
}

export function advanceRound(): void {
  sendMessage({ type: 'advance-round' });
}

export function startCustomDeck(): void {
  sendMessage({ type: 'start-custom-deck' });
}

export function submitCards(playerId: string, cards: { title: string; description: string; points: number }[]): void {
  sendMessage({ type: 'submit-cards', playerId, cards });
}

export function claimPlayer(playerId: string): void {
  sendMessage({ type: 'claim-player', playerId });
}

export function unclaimPlayer(playerId: string): void {
  sendMessage({ type: 'unclaim-player', playerId });
}

export function leaveSession(): void {
  sendMessage({ type: 'leave-session' });
  clearActiveSession();
  disconnect();
}

// --- Internal ---

function handleServerMessage(msg: ServerMessage): void {
  switch (msg.type) {
    case 'session-created':
      myConnectionId = msg.connectionId;
      sessionCode = msg.sessionCode;
      saveActiveSession(msg.sessionCode);
      break;
    case 'state-update':
      // Update session timestamp on every state-update (keeps session alive)
      if (sessionCode) saveActiveSession(sessionCode);
      stateListeners.forEach((fn) => fn(msg.state));
      break;
    case 'device-joined':
      // If this is our own join confirmation, save the active session
      if (sessionCode) saveActiveSession(sessionCode);
      break;
  }

  // Forward all events to listeners
  eventListeners.forEach((fn) => fn(msg));
}

function attemptReconnect(): void {
  if (reconnectAttempts >= MAX_RECONNECT) {
    console.log('[ws] Max reconnect attempts reached');
    return;
  }

  reconnectAttempts++;
  const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
  console.log(`[ws] Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT})`);

  setTimeout(() => {
    connect().then(() => {
      // After reconnect, re-join the session if we had one active
      const active = getActiveSession();
      if (active && sessionCode) {
        console.log(`[ws] Auto-rejoining session ${sessionCode} after reconnect`);
        sendMessage({ type: 'rejoin-session', sessionCode, deviceId: getOrCreateDeviceId() });
      }
    }).catch(() => {
      // Will retry via onclose handler
    });
  }, delay);
}
